import { LogLevel } from './types';

export type EluveErrorArgs = {
  error?: Error;
  metadata?: Record<string, unknown>;
};

/**
 * Keys under which a thrown value commonly nests the "real" error: Apollo puts
 * a link/auth failure on `networkError`, and EluveError carries the underlying
 * cause on `cause`. `isInstanceOf` walks these so a wrapped error still matches.
 */
const WRAPPER_KEYS = ['cause', 'networkError'] as const;
const MAX_UNWRAP_DEPTH = 5;

const hasErrorName = (
  value: unknown,
  expected: string,
  depth: number,
): boolean => {
  if (!value || typeof value !== 'object' || depth < 0) return false;
  if ((value as { name?: unknown }).name === expected) return true;
  return WRAPPER_KEYS.some((key) =>
    hasErrorName((value as Record<string, unknown>)[key], expected, depth - 1),
  );
};

export abstract class EluveError extends Error {
  /**
   * Stable, minification-proof identity for this error class. Each subclass
   * overrides it with a string literal, and the instance `name` is derived
   * from it so there is a single source of truth. Used by `isInstanceOf` to
   * recognise an error across bundle boundaries, where `instanceof` fails
   * because the class is defined more than once (e.g. the extension's
   * offscreen and background bundles each get their own copy).
   */
  static readonly errorName: string = 'EluveError';

  abstract readonly logLevel: LogLevel;
  override readonly name: string;
  override readonly cause?: Error;
  readonly metadata?: Record<string, unknown>;

  constructor(message: string, args?: EluveErrorArgs) {
    super(message);
    // Assigned (not a class-field initializer) so `name` stays an own,
    // enumerable property — preserving how errors serialize into logs.
    this.name = (this.constructor as typeof EluveError).errorName;
    this.cause = args?.error;
    this.metadata = args?.metadata;
  }

  /**
   * Robust nominal instance check. Prefer this over `instanceof` for EluveError
   * subclasses: it matches a real instance, a structurally-identical error from
   * a different bundle (same `errorName`, different class identity), and errors
   * wrapped under `cause` / `networkError`.
   *
   * @example
   * if (SessionUnavailableError.isInstanceOf(err)) { ... }
   */
  static isInstanceOf<T extends EluveError>(
    this: { readonly errorName: string; readonly prototype: T },
    error: unknown,
  ): error is T {
    if (error instanceof (this as unknown as new (...args: never[]) => T)) {
      return true;
    }
    return hasErrorName(error, this.errorName, MAX_UNWRAP_DEPTH);
  }

  getLogDetails = () => {
    return {
      name: this.name,
      errorMessage: this.cause?.message,
      stack: this.cause?.stack,
      message: this.message,
      metadata: this.metadata,
    };
  };
}
